import { MailDirection, ProcessingStatus } from "./EmailSearchAPI";

/**
 * The one support action each direction offers.
 *
 * Not a per-email choice: the route is fixed by direction, and an inbound
 * email has no Retry any more than an outbound one has a Reprocess.
 */
export type EmailAction = "Reprocess" | "Retry";

export const ACTION_FOR: Record<MailDirection, EmailAction> = {
    Inbound: "Reprocess",
    Outbound: "Retry",
};

/**
 * What a status means for the direction it was read on.
 *
 * `foreign` is not padding. `ProcessingStatus` is one union across both
 * pipelines, so four of its nine values cannot occur on an inbound email and
 * two cannot occur on an outbound one. Calling those `inFlight` would be a
 * lie that reads as deliberate; naming them makes each table say which
 * statuses are actually its own.
 */
type StatusStanding = "actionable" | "inFlight" | "succeeded" | "foreign";

/**
 * Inbound standing. `actionable` is `ProcessingStatusClassification`'s
 * `InboundTerminal` — [Failed, DeadLettered] — read from
 * `Chouette.EMS.Domain/MailProcessing/ProcessingStatusClassification.cs`.
 */
const INBOUND_STANDING: Record<ProcessingStatus, StatusStanding> = {
    Failed: "actionable",
    DeadLettered: "actionable",
    Queued: "inFlight",
    Processing: "inFlight",
    Completed: "succeeded",
    Building: "foreign",
    Sendable: "foreign",
    Sent: "foreign",
    Abandoned: "foreign",
};

/** Outbound standing. `actionable` is `OutboundTerminal` — [Failed, DeadLettered, Abandoned]. */
const OUTBOUND_STANDING: Record<ProcessingStatus, StatusStanding> = {
    Failed: "actionable",
    DeadLettered: "actionable",
    Abandoned: "actionable",
    Queued: "inFlight",
    Building: "inFlight",
    Sendable: "inFlight",
    Sent: "succeeded",
    Processing: "foreign",
    Completed: "foreign",
};

const STANDING: Record<MailDirection, Record<ProcessingStatus, StatusStanding>> = {
    Inbound: INBOUND_STANDING,
    Outbound: OUTBOUND_STANDING,
};

/**
 * Statuses that are in-flight in their OWN pipeline, independent of which
 * direction is asking.
 *
 * This is NOT the same split as `StatusStanding.inFlight`, which is scoped
 * per direction and therefore calls `Sendable` and `Sent` the same thing on
 * Inbound — both `"foreign"` — even though only one of them is genuinely
 * still moving. `stalled` measures real elapsed time against a status's own
 * pipeline, so the question "does stalled mean anything here" has to be
 * answered by the status itself, not by the direction viewing it: `Building`
 * and `Sendable` are the outbound in-flight pair the same way `Queued` and
 * `Processing` are the inbound one, and a stalled `Sendable` read from an
 * inbound row is exactly the cross-direction case spec §3.1 says Reprocess
 * must still catch. `Completed` and `Sent`, by contrast, are each a
 * pipeline's own success state — nothing "stalls" into having succeeded — so
 * neither belongs here regardless of which direction is asking.
 *
 * A `Record` rather than a `Set`, for the same reason the two tables above
 * are: a tenth `ProcessingStatus` must not be able to default silently. The
 * standing tables would force a compiler error and bring a reader into this
 * file, but nothing would then make them answer THIS question too — and a new
 * status quietly defaulting to "cannot be stalled" is precisely how the
 * clause below lost its status check in the first place.
 *
 * Deliberately NOT derived from the `inFlight` entries of the two standing
 * tables. Those happen to union to the same four values today, and the whole
 * argument above is that per-direction standing and a status's own nature are
 * different questions. Deriving one from the other would encode a
 * coincidence as a guarantee.
 */
const NATIVELY_IN_FLIGHT: Record<ProcessingStatus, boolean> = {
    Queued: true,
    Processing: true,
    Building: true,
    Sendable: true,
    Failed: false,
    DeadLettered: false,
    Abandoned: false,
    Completed: false,
    Sent: false,
};

export type ActionVerdict = { action: EmailAction } & ({ available: true } | { available: false; why: string });

/**
 * Whether this email's one action can be offered, and if not, why not.
 *
 * THE TWO DIRECTIONS DO NOT SHARE A RULE, and the difference is the whole
 * point of this file (spec §3.1, §3.2):
 *
 * - Reprocess accepts a settled inbound failure **or a stalled email**. Its
 *   handler comment says the stalled clause is "most of what this endpoint is
 *   for", and is explicit that collapsing it into the shared terminal set
 *   would quietly remove it.
 * - Retry accepts a settled outbound failure and **ignores `stalled`
 *   entirely**. A stalled `Sendable` may still be genuinely in flight, and is
 *   a mailbox-level problem rather than a per-email one.
 *
 * `stalled` is read from the server's response, never computed here — it is
 * a read-time overlay and contract §2.3 forbids re-deriving it.
 *
 * The caller disables the control on `available: false` and renders `why`
 * beside it, so none of the `400`s these rules mirror is ever reached.
 */
export function actionFor(
    direction: MailDirection,
    status: ProcessingStatus | undefined,
    stalled: boolean,
): ActionVerdict {
    const action = ACTION_FOR[direction];

    // Nullable on the wire on both shapes. Never observed absent across the
    // dev tenant, but the type admits it and guessing an eligibility from a
    // status we do not have would offer a write on no evidence.
    if (status === undefined) {
        return {
            action,
            available: false,
            why: `This email reported no status, so this page cannot tell whether ${action.toLowerCase()} is safe.`,
        };
    }

    const standing = STANDING[direction][status];

    if (standing === "actionable") {
        return { action, available: true };
    }

    // Inbound only. The clause that makes Reprocess wider than terminality —
    // gated on the status's OWN nature (NATIVELY_IN_FLIGHT), not on the
    // per-direction `standing` above. `standing` alone cannot carry this:
    // `Sendable` and `Sent` are both merely "foreign" to Inbound, yet a
    // stalled `Sendable` is exactly the cross-direction case Reprocess must
    // still catch, while a stalled `Sent` reports nothing to reprocess (it is
    // Outbound's own terminal success, not a status that has stopped moving).
    if (direction === "Inbound" && stalled && NATIVELY_IN_FLIGHT[status]) {
        return { action, available: true };
    }

    return { action, available: false, why: whyNot(direction, status, standing, stalled) };
}

function whyNot(
    direction: MailDirection,
    status: ProcessingStatus,
    standing: Exclude<StatusStanding, "actionable">,
    stalled: boolean,
): string {
    if (standing === "succeeded") {
        return direction === "Inbound"
            ? `This email completed processing. There is nothing to reprocess.`
            : `This email was sent. There is nothing to retry.`;
    }

    if (standing === "foreign") {
        const other = direction === "Inbound" ? "outbound" : "inbound";
        return `${status} is an ${other} status, so ${ACTION_FOR[direction].toLowerCase()} does not apply to this email.`;
    }

    // in-flight. Inbound never reaches here while stalled — actionFor returned
    // available above — so the stalled branch below is outbound only.
    if (stalled) {
        return (
            `${status} has not progressed for a while, but retry accepts only a settled failure. ` +
            `A stalled send is a mailbox-level problem rather than a per-email one — look at Mail processing, ` +
            `under mailboxes, instead.`
        );
    }

    return direction === "Inbound"
        ? `${status} is still in flight. Reprocess is offered once it fails, or once it has been stuck long enough to count as stalled.`
        : `${status} is still in flight. Retry is offered once it settles as a failure.`;
}
